import { useState } from "react";
import { ArrowUpRight, Info } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import GuidebookCard from "../components/GuidebookCard";
import { SHOP_URL, IMAGES, GUIDEBOOKS } from "../data/content";

const CATEGORIES = [
  "All Editions",
  "Remote Viewing",
  "Astral Projection",
  "Field Workbooks",
];

export default function Guidebooks() {
  usePageMeta(
    "Guidebooks — Field Manuals & Codexes | Consciousness Ascending",
    "Browse guidebook pathways for remote viewing and astral projection. All purchases are fulfilled through our official Fourthwall shop."
  );
  const [active, setActive] = useState("All Editions");
  const filtered =
    active === "All Editions"
      ? GUIDEBOOKS
      : GUIDEBOOKS.filter((b) => b.category === active);

  return (
    <>
      <PageHero
        eyebrow="The Library // Guidebooks"
        lines={["Field manuals &", "sacred editions."]}
        description="All educational materials are engineered as comprehensive digital and physical volumes — from first ideograms to full astral cartography. Purchases are handled securely on our official Fourthwall storefront."
        image={IMAGES.books}
        imageAlt="Field manuals under the spotlight"
        imageAspect="aspect-[4/3]"
        meta="EDITIONS: DIGITAL PDF // EPUB // ARCHIVAL PRINT"
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10 lg:py-28" data-testid="guidebooks-catalog">
        <Reveal>
          <div
            className="flex flex-wrap items-center gap-3"
            role="tablist"
            aria-label="Filter guidebooks by category"
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={active === cat}
                data-testid={`guidebooks-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`border px-5 py-2.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                  active === cat
                    ? "border-[#8B5CF6] bg-[#8B5CF6]/15 text-[#F0F2F8] shadow-[0_0_20px_rgba(139,92,246,0.25)]"
                    : "border-white/15 text-[#8E95A5] hover:border-[#8B5CF6]/50 hover:text-[#F0F2F8]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((book, i) => (
            <Reveal key={book.id} delay={0.07 * i} className="h-full">
              <GuidebookCard book={book} />
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-12 text-sm text-[#5F6677]">
            No editions in this category yet — new volumes are added through the
            Fourthwall storefront.
          </p>
        )}
      </section>

      <section className="border-t border-white/[0.06] bg-[#0C0E14]/40">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-10 lg:py-32">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">How Purchasing Works</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-6 text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl">
                Everything is fulfilled by Fourthwall.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="body-muted mt-6 max-w-xl text-sm leading-relaxed sm:text-base">
                Fourthwall is our official production and fulfillment partner.
                When you select a guidebook above, you complete the purchase on
                their secure storefront — encrypted checkout, global tracked
                dispatch for print editions, and instant download for digital
                volumes.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.25}>
              <div className="border border-white/[0.08] bg-[#0F121B]/70 p-8">
                <p className="flex items-center gap-3 text-[11px] font-mono-ui uppercase tracking-[0.22em] text-[#A78BFA]">
                  <Info className="h-4 w-4" /> Good to know
                </p>
                <ul className="mt-6 space-y-4 text-sm text-[#8E95A5]">
                  <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>Digital files arrive instantly by email</li>
                  <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>Print editions ship worldwide, tracked</li>
                  <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>Prices and formats are listed per title in the shop</li>
                </ul>
                <a
                  href={SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guidebooks-shop-cta-btn"
                  className="mt-8 inline-flex w-full items-center justify-center gap-3 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-6 py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30 hover:shadow-[0_0_28px_rgba(139,92,246,0.4)]"
                >
                  Open the Fourthwall Shop
                  <ArrowUpRight className="h-4 w-4 text-[#A78BFA]" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
